import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import type { BranchDetail, Catalog, CommitDetail, CommitView, DiffView, GalleryItem, LiveView, NodeHistoryEntry, RepoView } from "./types";

export const repoKeys = {
  gallery: ["workflow-repos"] as const,
  repo: (key: string) => ["workflow-repos", key] as const,
  log: (key: string) => ["workflow-repos", key, "log"] as const,
  branch: (key: string, branch: string) => ["workflow-repos", key, "branch", branch] as const,
  commit: (key: string, id: string) => ["workflow-repos", key, "commit", id] as const,
  live: (key: string) => ["workflow-repos", key, "live"] as const,
};

const base = (key: string) => `/api/workflows/repos/${encodeURIComponent(key)}`;

export function useGallery() {
  const api = useApi();
  return useQuery({ queryKey: repoKeys.gallery, queryFn: () => api.get<GalleryItem[]>("/api/workflows/repos") });
}

export function useRepo(key: string) {
  const api = useApi();
  return useQuery({ queryKey: repoKeys.repo(key), queryFn: () => api.get<RepoView>(base(key)) });
}

export function useLog(key: string, enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: repoKeys.log(key), queryFn: () => api.get<CommitView[]>(`${base(key)}/log`, { limit: 300 }), enabled });
}

export function useBranch(key: string, branch: string) {
  const api = useApi();
  return useQuery({ queryKey: repoKeys.branch(key, branch), queryFn: () => api.get<BranchDetail>(`${base(key)}/branches/${branch}`), staleTime: Infinity, refetchOnWindowFocus: false });
}

export function useCommit(key: string, id: string | null) {
  const api = useApi();
  return useQuery({ queryKey: repoKeys.commit(key, id ?? ""), queryFn: () => api.get<CommitDetail>(`${base(key)}/commits/${id}`), enabled: !!id, staleTime: Infinity });
}

export function useLive(key: string) {
  const api = useApi();
  return useQuery({ queryKey: repoKeys.live(key), queryFn: () => api.get<LiveView | null>(`${base(key)}/live`), refetchInterval: 60_000 });
}

export function useDiff(key: string, from: string | null, to: string | null) {
  const api = useApi();
  return useQuery({ queryKey: ["workflow-repos", key, "diff", from, to], queryFn: () => api.get<DiffView>(`${base(key)}/diff`, { from: from!, to: to! }), enabled: !!from && !!to && from !== to, staleTime: Infinity });
}

export function useNodeHistory(key: string, nodeId: string | null) {
  const api = useApi();
  return useQuery({ queryKey: ["workflow-repos", key, "node-history", nodeId], queryFn: () => api.get<NodeHistoryEntry[]>(`${base(key)}/nodes/${encodeURIComponent(nodeId!)}/history`), enabled: !!nodeId });
}

export function useCatalog() {
  const api = useApi();
  return useQuery({ queryKey: ["workflow-builder", "catalog"], queryFn: () => api.get<Catalog>("/api/workflows/builder/catalog"), staleTime: 300_000 });
}

export const repoPaths = {
  base,
  commits: (key: string, branch: string) => `${base(key)}/commits?branch=${encodeURIComponent(branch)}`,
  workingCopy: (key: string, branch: string) => `${base(key)}/working-copy?branch=${encodeURIComponent(branch)}`,
  branchByName: (key: string, name: string) => `${base(key)}/branch?name=${encodeURIComponent(name)}`,
};
